import { PlaceInGlobalNameSpace } from './Utils'

export interface IAkixiError extends Error {
    description?: string
    lineNumber?: string
    fileName?: string
    stacktrace?: string
}

/**
 * Returns true if the specified JavaScript variable is "empty".
 *
 * @param pVar The variable to be tested.
 * @returns {@code true} if the variable's value is either null or undefined.
 */
export function IsVarEmpty<T>(pVar: T | undefined | null): pVar is null | undefined {
    return pVar === undefined || pVar === null
}

/**
 * Returns true if the specified JavaScript variable is not "empty".
 *
 * @param variable - The variable to check.
 * @returns {@code false} if the variable's value is either null or undefined.
 */
export function isVarNotEmpty<T>(variable: Nullable<T>): variable is NonNullable<T> {
    return variable !== undefined && variable !== null
}

/**
 * Returns trinary state for an or.
 */
export function Or<T>(...args: T[]): Definable<boolean> {
    if (args.some(IsVarEmpty)) {
        return undefined
    }

    return args.some(Boolean)
}

/**
 * Returns true if the specified value does not provide a valid text value containing 1 or more characters.
 *
 * Note that this method treats the specified input value as if it might potentially NOT be a primitive String value - the function is
 * specifically designed to work with String objects as well as primitive Strings values. Any valid JavaScript type can be specified
 * as the input argument, where it's toString() method is then used (combined with automatic coercion) in order to extact its primitive
 * string type representation.
 *
 * Therefore this method can return an unexpected value of true for Object types and Array instances, as their toString() method
 * implementation will often return a non-empty primitive String value.
 *
 * Note: When used in Typescript we restrict the input parameter types to purely be strings
 *
 * @param {string} sString String to be tested.
 * @returns {@code true} is the string is not empty, null or undefined.
 *
 */
export function IsStringEmpty(sString: string | null | undefined): sString is null | undefined {
    let bIsString = false
    if (sString !== undefined && sString !== null) {
        // Ensure we have a primitive String type.
        // We could also do "sStringNative = String(sString);", see sub-section 15.5.1.1 in the ECMAScript Language Specification 5.1.
        const sStringNative = sString.toString()
        // Obtain character length, where JavaScript coercion will temporarily create a String Object wrapper, e.g. the following line
        // is equivalent to "bIsString = ((new String(sStringNative)).length > 0);".
        bIsString = sStringNative.length > 0
    }
    return !bIsString
}

/**
 * Returns true if the specified value provides a valid text value containing 1 or more characters.
 * @param sString String to be tested.
 * @returns {@code true} is the string is not empty, null or undefined.
 * @see IsStringEmpty
 * @see IsVarEmpty
 */
export function isStringNotEmpty(sString: string | null | undefined): sString is string {
    return !IsStringEmpty(sString)
}

// Constant used to define whether we output advanced error information (e.g. file, line number, and stack trace) in the ExceptionHandle() method.
const bOUTPUT_DEBUG_INFO = false

// Default text variants for boolean values.
const sDEFAULT_BOOLEAN_TRUE1_ON = 'ON'
const sDEFAULT_BOOLEAN_TRUE2_TRUE = 'TRUE'
const sDEFAULT_BOOLEAN_TRUE3_1 = '1'
const sDEFAULT_BOOLEAN_TRUE4_YES = 'YES'

// Parses a value and return a boolean representation of it.
//
export function parseBool(pValue: string): boolean {
    let bReturn = false
    if (!IsVarEmpty(pValue)) {
        const sValue = pValue.toString()
        if (!IsStringEmpty(sValue)) {
            const sValueUpper = sValue.toUpperCase()
            bReturn =
                sValueUpper === sDEFAULT_BOOLEAN_TRUE1_ON ||
                sValueUpper === sDEFAULT_BOOLEAN_TRUE2_TRUE ||
                sValueUpper === sDEFAULT_BOOLEAN_TRUE3_1 ||
                sValueUpper === sDEFAULT_BOOLEAN_TRUE4_YES
        }
    }
    return bReturn
}

// Appropriately enable/disables the specified control.
//
export function ControlEnable(pCtrl: HTMLInputElement | null, bEnable: boolean): void {
    /* eslint-disable no-param-reassign */
    if (pCtrl !== null) {
        if (pCtrl.disabled === bEnable) {
            pCtrl.disabled = !bEnable
        }
    }
}

/*
 Utility function to toggle the visibility of the html element.
*/
export function ControlHide(pCtrl: HTMLElement | null, bHide: boolean): void {
    let pCurrentStyle = null
    if (pCtrl !== null) {
        pCurrentStyle = pCtrl.style.display
        if (bHide && pCurrentStyle !== 'none') {
            pCtrl.style.display = 'block'
        }
    }
}

/*
 Control action button visibility based on the current user's permissions.
*/
const pACTION_BUTTON_IDS_TO_CHECK = ['idbtnAdd', 'idbtnChange', 'idbtnDelete']

export function ManageActionButtonsBasedOnUserPermissions(pDocument: Document | null, bIsReadOnly: boolean): void {
    // TODO - This method need update after user permissions added into currentUser API, At the moment it has code to
    // enable disable view button.
    if (pDocument !== null) {
        if (bIsReadOnly) {
            for (let i = 0; i < pACTION_BUTTON_IDS_TO_CHECK.length; i++) {
                const pBtnId = pACTION_BUTTON_IDS_TO_CHECK[i]
                const pBtnAction = pDocument.getElementById(pBtnId)
                ControlHide(pBtnAction, true)
            }
        } else {
            const pBtnView = pDocument.getElementById('idbtnView')
            ControlHide(pBtnView, true)
        }
    }
}

// Return true if the two specified values identically match once converted to primitive String types.
// Note that null, undefined objects, and empty strings are deliberately considered identical.
//
// Similar to the IsStringEmpty() function, this method treats the specified input arguments as if they might not be underlying primitive
// String values, where the function is designed to work with String objects as well as primitive Strings values, and interchangeably
// within either argument.
// Any valid JavaScript type can be specified within the input argument(s), where the toString() method is then used (combined with
// automatic coercion) in order to extact its primitive String type representation.
//
export function IsStringMatch(sString1: string, sString2: string): boolean {
    let bIsMatch = false
    //
    const bString1Empty = IsStringEmpty(sString1)
    const bString2Empty = IsStringEmpty(sString2)
    //
    if (bString1Empty && bString2Empty) bIsMatch = true
    else if (!bString1Empty && !bString2Empty) bIsMatch = sString1.toString() === sString2.toString()
    //
    return bIsMatch
}

// Returns true if the specified value is an underlying JavaScript String primitive value type.
//
export function IsStringPrimitive(sString: string): sString is string {
    return !IsVarEmpty(sString) && typeof sString === 'string'
}

// Returns true if the specified value is an underlying JavaScript String primitive value and also specifically does NOT contain an empty
// text value (the value must be 1 or more characters in length).
//
export function IsStringPrimitiveNonEmpty(sString: string): sString is string {
    return IsStringPrimitive(sString) && sString.length > 0
}

// Generates a string using the given string of characters repeated the specified number of times.
//
export function StringRepeat(sStringToRepeat: string, iRepeatCount: number): string {
    let sReturn = ''
    for (let i = 0; i < iRepeatCount; i += 1) {
        sReturn += sStringToRepeat
    }
    return sReturn
}

// Returns true if the specified character is a number.
//
export function IsNumberChr(sChr: string, bDecimalChrValid: boolean): boolean {
    let bIsCharNumber = false
    switch (sChr) {
        case '0':
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9':
            bIsCharNumber = true
            break
        case '.':
            bIsCharNumber = bDecimalChrValid
            break
        default:
    }
    return bIsCharNumber
}

// Parses the specified string and automatically expands any contained internal placeholder references embedded within the
// string in "|x" format using the corresponding items from the specified array of string values. x is a number equal or higher than 1
// (e.g. "Error encountered performing transaction on table |1 - Field name |2 doesn't exist in table |1.").
//
export function StringExpandPlaceholders(sValue: string, pReplaceWith: string[]): string {
    let sReturn = sValue
    if (!IsStringEmpty(sValue)) {
        const iReplaceWithCount = IsVarEmpty(pReplaceWith) ? 0 : pReplaceWith.length
        let sChr
        let bInPlaceholder = false
        let sPlaceholderNum = ''
        let iPlaceholderNum
        sReturn = ''
        // Parse the specified text value.
        for (let i = 0; i < sValue.length; i += 1) {
            sChr = sValue.charAt(i)
            if (sChr === '|') {
                // Found a placeholder character.
                if (bInPlaceholder) {
                    // Already in a placeholder sequence.
                    if (IsStringEmpty(sPlaceholderNum)) {
                        // Two placeholder characters on their own, which we interpret as an escape sequence for inclusion of the pipe character itself.
                        sReturn += '|'
                        bInPlaceholder = false
                    } else {
                        // Placeholder sequence with numbers being terminated.
                        iPlaceholderNum = IsStringEmpty(sPlaceholderNum) ? 0 : parseInt(sPlaceholderNum, 10) - 1
                        if (iPlaceholderNum < 0) iPlaceholderNum = 0
                        if (iPlaceholderNum < iReplaceWithCount && !IsVarEmpty(pReplaceWith[iPlaceholderNum]))
                            sReturn += pReplaceWith[iPlaceholderNum]
                        // Start new placeholder sequence (leave bInPlaceholder = true).
                    }
                } else {
                    // Start new placeholder sequence.
                    bInPlaceholder = true
                }
                sPlaceholderNum = ''
            } else if (bInPlaceholder) {
                // A placeholder sequence has previously been started.
                if (IsNumberChr(sChr, false)) {
                    // More number characters for placeholder sequence.
                    sPlaceholderNum += sChr
                } else {
                    // Normal character not part of placeholder sequence.
                    // Perform the appropriate replacement operation for the terminating placeholder sequence.
                    iPlaceholderNum = IsStringEmpty(sPlaceholderNum) ? 0 : parseInt(sPlaceholderNum, 10) - 1
                    if (iPlaceholderNum < 0) iPlaceholderNum = 0
                    if (iPlaceholderNum < iReplaceWithCount && !IsVarEmpty(pReplaceWith[iPlaceholderNum]))
                        sReturn += pReplaceWith[iPlaceholderNum]
                    bInPlaceholder = false
                    sPlaceholderNum = ''
                    // Add normal character.
                    sReturn += sChr
                }
            } else {
                // Normal character not part of placeholder sequence.
                sReturn += sChr
            }
        }
        if (bInPlaceholder) {
            // Value ends in a placeholder sequence.
            iPlaceholderNum = IsStringEmpty(sPlaceholderNum) ? 0 : parseInt(sPlaceholderNum, 10) - 1
            if (iPlaceholderNum < 0) iPlaceholderNum = 0
            if (iPlaceholderNum < iReplaceWithCount && !IsVarEmpty(pReplaceWith[iPlaceholderNum]))
                sReturn += pReplaceWith[iPlaceholderNum]
        }
    }
    return sReturn
}

// Returns true if the specified value is numeric.
// The method gracefully returns false for null, undefined, as well as "empty" text values that have 0 characters in length.
// Note: I've deliberately abused the guard function response type to allow Typescript to correctly
// handle the if (IsNumeric(string){ parseInt(string);}) case with a possibly null string.
export function IsNumeric(sText: string | null | undefined): sText is string {
    /* eslint-disable no-restricted-globals */
    const fl = IsStringEmpty(sText) ? null : parseFloat(sText)
    return fl !== null && !isNaN(fl) && isFinite(fl)
}

// Returns the mask result of two large positive integer values and'd together.
// The largest supported bit value is 53 bits only (0x001FFFFFFFFFFFFF) due to JavaScript internally storing numbers as a 64 bit IEEE 754 floating point number type.
// If larger or negative values are specified, then the method returns zero.
//
export function Integer64BitwiseAnd(iValue1: number, iValue2: number): number {
    /* eslint-disable no-bitwise */
    let iResult = 0
    //
    const iVALUE_MAX = 0x001fffffffffffff
    if (iValue1 > 0 && iValue2 > 0 && iValue1 <= iVALUE_MAX && iValue2 <= iVALUE_MAX) {
        // Define a 31 bit mask without negation.
        const iMASK_32 = 0x7fffffff
        // Define  a divisor to handle chunks of 31 bits at a time.
        const iDIVISOR = 0x80000000 // Effectively (1 << 31);
        //
        // Calculate mask combination using current 31 bits.
        iResult = iMASK_32 & iValue1 & (iMASK_32 & iValue2)
        // Extract next 31 bits of both values.
        const iValue1Floored = Math.floor(iValue1 / iDIVISOR) // Effectively (iValue1 >>> 31);
        const iValue2Floored = Math.floor(iValue2 / iDIVISOR) // Effectively (iValue2 >>> 31);
        if (iValue1Floored !== 0 && iValue2Floored !== 0) {
            let iResultFragment = iMASK_32 & iValue1Floored & (iMASK_32 & iValue2Floored)
            iResultFragment *= iDIVISOR // Effectively (iResultFragment << 31);
            iResult += iResultFragment
        }
    }
    return iResult
}

// Returns true if the specified e-mail address is probably valid according to the RFC-822 specification.
//
export function IsValidEmailAddress(sEmailAddress: string): boolean {
    let bIsValid = false
    //
    if (!IsStringEmpty(sEmailAddress)) {
        // Build appropriate regular expression for the e-mail address specification.
        //
        const sQText = '[^\\x0d\\x22\\x5c\\x80-\\xff]'
        const sDText = '[^\\x0d\\x5b-\\x5d\\x80-\\xff]'
        const sAtom = '[^\\x00-\\x20\\x22\\x28\\x29\\x2c\\x2e\\x3a-\\x3c\\x3e\\x40\\x5b-\\x5d\\x7f-\\xff]+'
        const sQuotedPair = '\\x5c[\\x00-\\x7f]'
        const sDomainLiteral = `\\x5b(${sDText}|${sQuotedPair})*\\x5d`
        const sQuotedString = `\\x22(${sQText}|${sQuotedPair})*\\x22`
        const sDomainReference = sAtom
        const sSubDomain = `(${sDomainReference}|${sDomainLiteral})`
        const sWord = `(${sAtom}|${sQuotedString})`
        const sDomain = `${sSubDomain}(\\x2e${sSubDomain})+` // Domains must include a TLD
        const sLocalPart = `${sWord}(\\x2e${sWord})*`
        const sIPAddress = '\\[?(?:\\d{1,3}\\.){3}\\d{1,3}\\]?'
        const sDomainOrIP = `(${sDomain}|${sIPAddress})`
        // Complete RFC-822 e-mail address specification.
        const sAddressSpecification = `${sLocalPart}\\x40${sDomainOrIP}`
        // As entire regular expression string.
        const sValidationExp = `^${sAddressSpecification}$`
        //
        // Validate the specified address.
        const pRegExp = new RegExp(sValidationExp)
        bIsValid = pRegExp.test(sEmailAddress)
    }
    return bIsValid
}

// Return the n characters of the left side of a text string.
//
export function StrLeft(sValue: string, iCharCount: number): string {
    const bIsValueEmpty = IsStringEmpty(sValue)
    const iLength = bIsValueEmpty ? 0 : sValue.length
    let sRetValue = sValue
    if (iLength > iCharCount) sRetValue = sRetValue.substr(0, iCharCount)
    return sRetValue
}

// Return the n characters of the right side of a text string.
//
export function StrRight(sValue: string, iCharCount: number): string {
    const bIsValueEmpty = IsStringEmpty(sValue)
    const iLength = bIsValueEmpty ? 0 : sValue.length
    let sRetValue = sValue
    if (iLength > iCharCount) sRetValue = sRetValue.substr(iLength - iCharCount)
    return sRetValue
}

// Pads the beginning of the specified text string.
//
export function PadLeft(sValue: string, iPadLen: number, sPadChr: string): string {
    const bIsValueEmpty = IsStringEmpty(sValue)
    const iLength = bIsValueEmpty ? 0 : sValue.length
    let sRetValue = sValue
    for (let i = iLength; i < iPadLen; i += 1) {
        sRetValue = sPadChr + sRetValue
    }
    return sRetValue
}

// Pads the end of the specified text string.
//
export function PadRight(sValue: string, iPadLen: number, sPadChr: string): string {
    const bIsValueEmpty = IsStringEmpty(sValue)
    const iLength = bIsValueEmpty ? 0 : sValue.length
    let sRetValue = sValue
    for (let i = iLength; i < iPadLen; i += 1) {
        sRetValue += sPadChr
    }
    return sRetValue
}

// Replaces all occurrences of the specified sub-string using the JavaScript RegExp regular expression parser class.
// Note that input characters sequences to search for & replace specified via the sSubStrToReplace, must be compatible with the
// RegExp regular expression parser class, and should be escaped or specified accordingly. For example, to search for and replace
// "$", "\\$" must be specified as a literal string definition (the actual escape sequence to the regular expression parser is
// "\$").
// See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions for more information on which character
// sequences have a special meaning to the JavaScript RegExp implementation.
//
export function StringReplaceAll(sStrSearchIn: string, sSubStrToReplace: string, sReplaceWith: string): string {
    let sReturn = sStrSearchIn
    if (!IsStringEmpty(sStrSearchIn)) {
        sReturn = sStrSearchIn.toString()
        const pRegExp = new RegExp(sSubStrToReplace, 'g')
        sReturn = sReturn.replace(pRegExp, sReplaceWith)
    }
    return sReturn
}

// Replaces all occurrences of the sub-text token sSubStrToReplace being found within the overall text value sStrSearchIn, which
// represents a combined sequence of sub-string values separated by the character sequence sStrSeparatorSequence.
// For example, an overall text value of "100,200,201,203,210,200", using a single comma separator character sequence, a search
// value specified of "200" and also a replacement value of "300", would yield a return value of "100,300,201,203,210,300".
//
// The sStrSearchIn argument in addition to the sStrSeparatorSequence, sSubStrToReplace, and sReplaceWith arguments must all be
// specified as primitive JavaScript String values, otherwise they are assumed to individually specify no text value at all.
// If the sStrSeparatorSequence, sSubStrToReplace, and sReplaceWith arguments all depict an empty or null value, then the method
// returns the original overall text value and effectively performs no action.
// Giving a null or empty sReplaceWith argument value only, just removes all occurrences of the search sub-value sSubStrToReplace
// from the overall text value.
//
// The function returns the overall resultant text value sequence with the token sSubStrToReplace replaced by the value given by the
// sReplaceWith argument. Alternatively, the sReplaceWith argument's value is singularly appended to the end of the original
// overall value, based on the search sub-token values (sSubStrToReplace or sReplaceWith) not being actually found and when the
// bAddIfNotFound argument is also set to true.
//
// Note that at each stage of the function checks are implemented to avoid pushing empty values into the output array.
//
// Step 1 of function consists of a loop that handles the functions primary task to "find and replace a value". Each array element
// is evaluated to determine if it matches either sSubStrToReplace, sReplaceWith or is empty and sets a corresponding flag
// (bIsSubStrToReplaceFound or bIsReplaceWithFound) that is referenced within Step 2. Each loop iteration pushes a new value to the
// output array pArrayOfSubStrsNew if the current array sSubStr and sReplaceWith values are not empty.
//
// Step 1 - Loop truth table.
//
// sSubStr === sSubStrToReplace | sSubStr === sReplaceWith |=>| Token to push | bIsSubStrToReplaceFound | bIsReplaceWithFound |
// ----------------------------------------------------------------------------------------------------------------------------
//                 true             |            true           |=>| sReplaceWith  |           true                |          true        |
//                 true             |            false           |=>| sReplaceWith  |           true                |          false       |
//                 false             |            true           |=>|   sSubStr     |           false            |          true        |
//                 false             |            false           |=>|   sSubStr     |           false            |          false       |
//
// Step 2 - Conditional block truth table.
//
// bIsSubStrToReplaceFound | bIsReplaceWithFound | bAddIfNotFound |=>| Token to push |
// -----------------------------------------------------------------------------------
//              true            |          true          |         true       |=>|         N/A      |
//              true            |          true          |         false       |=>|         N/A      |
//              true            |          false          |         true       |=>|         N/A      |
//              true            |          false          |         false       |=>|         N/A      |
//              false            |          true          |         true       |=>|         N/A      |
//              false            |          true          |         false       |=>|         N/A      |
//              false            |          false          |         true       |=>|  sReplaceWith |
//              false            |          false          |         false       |=>|         N/A      |
//
// Step 2 addresses requirement to add the sReplaceWith value to the end of the output, if requested with bAddIfNotFound. It uses
// conditional checks to avoid sReplaceWith value from being duplicated (ie. if it is already present in the output). It checks
// against the flags (bIsSubStrToReplaceFound and bIsReplaceWithFound) set in the previous loop (Step 1).
// In the truth table, N/A means 'do nothing'.
//
// Step 3 - Join array.
//
// Returns a string value based on the list of sub-token value/s within pArrayOfSubStrsNew, array join method is called if the array
// has multiple elements, ie. > 0, with sStrSeparatorSequence (if valid otherwise an empty string's) used as seperator value
// between each element of array (if applicable). Otherwise if pArrayOfSubStrsNew array has only a single value the sReplaceWith
// value is returned if bAddIfNotFound is true.
//
export function StringReplaceAllInChrsSeparatedList(
    sStrSearchIn: string,
    sStrSeparatorSequence: string,
    sSubStrToReplace: string,
    sReplaceWith: string,
    bAddIfNotFound: boolean
): string {
    let sStrReturn = sStrSearchIn

    // Verify whether we have a non-empty primitive String value for the replacement token value.
    const bIsReplaceWithNonEmpty = IsStringPrimitiveNonEmpty(sReplaceWith)

    // Define variable that tracks if we found any sub-tokens matching "sSubStrToReplace".
    let bIsSubStrToReplaceFound = false
    // Define variable that tracks if we found any sub-tokens matching "sReplaceWith".
    let bIsReplaceWithFound = false
    // Define new array which will hold all candidate sub-token values that we'll return re-built as a new overall text value.
    const pArrayOfSubStrsNew = []

    // Step 1.
    // Verify we have an overall text value to search within.
    if (IsStringPrimitiveNonEmpty(sStrSearchIn)) {
        //
        // Ensure we have non-empty primitive String values for the separator character sequence and search token argument values.
        if (IsStringPrimitiveNonEmpty(sStrSeparatorSequence) && IsStringPrimitiveNonEmpty(sSubStrToReplace)) {
            //
            // Split the overall text value into individual sub-token values.
            //
            const pArrayOfSubStrsOld = sStrSearchIn.split(sStrSeparatorSequence)
            const iCountOfSubStrsOld = IsVarEmpty(pArrayOfSubStrsOld) ? 0 : pArrayOfSubStrsOld.length
            if (iCountOfSubStrsOld > 0) {
                // Search for the designated sub-token value.
                let sSubStr
                for (let i = 0; i < iCountOfSubStrsOld; i += 1) {
                    sSubStr = pArrayOfSubStrsOld[i]

                    if (sSubStr === sSubStrToReplace) {
                        if (bIsReplaceWithNonEmpty) {
                            // Add replacement sub-token instead in place of the sub-item value being searched for, which we just found.
                            pArrayOfSubStrsNew.push(sReplaceWith)
                        }

                        // Found sub-value matching sSubStrToReplace.
                        bIsSubStrToReplaceFound = true

                        if (sSubStr === sReplaceWith) {
                            // Found sub-value matching sReplaceWith.
                            bIsReplaceWithFound = true
                        }
                    } else if (IsStringEmpty(sSubStr)) {
                        // The sub-token value is an empty text value, which can occur when the original overall text value contains the
                        // separator sequence only.
                    } else {
                        // Not the sub-item value we were searching for, so this is returned within the resultant overall text list value.
                        pArrayOfSubStrsNew.push(sSubStr)

                        if (sSubStr === sReplaceWith) {
                            // Found sub-value matching sReplaceWith.
                            bIsReplaceWithFound = true
                        }
                    }
                }
            }
        } else if (!IsStringMatch(sStrSearchIn, sSubStrToReplace)) {
            // When we can't parse the overall text value for sub-tokens, then we add the original value as an individual sub-token item
            // itself if it does not match the replacement value, else it would otherwise be incorrectly removed from the returned
            // overall text value.
            pArrayOfSubStrsNew.push(sStrSearchIn)
        }
    }

    // Step 2.
    // If sSubStrToReplace and sReplaceWith are not found (flags bIsSubStrToReplaceFound and bIsReplaceWithFound set in Step 1) and requested
    // to be added by function parameter bAddIfNotFound (also checks for non empty value to avoid pushing an empty string).
    if (!bIsSubStrToReplaceFound && !bIsReplaceWithFound && bAddIfNotFound && bIsReplaceWithNonEmpty) {
        // Add the replacement sub-token value if not found, as depicted by the calling logic within the bAddIfNotFound argument.
        pArrayOfSubStrsNew.push(sReplaceWith)
    }

    // Step 3.
    // Generate a new overall text value to return based on the candidate list of sub-token values.
    const iCountOfSubStrsNew = IsVarEmpty(pArrayOfSubStrsNew) ? 0 : pArrayOfSubStrsNew.length
    if (iCountOfSubStrsNew > 0) {
        // Only generate a new return value if there are 1 or more sub-token to re-construct from.
        if (IsStringPrimitiveNonEmpty(sStrSeparatorSequence))
            sStrReturn = pArrayOfSubStrsNew.join(sStrSeparatorSequence)
        else sStrReturn = pArrayOfSubStrsNew.join('')
    } else if (bAddIfNotFound) {
        sStrReturn = sReplaceWith
    }
    //
    return sStrReturn
}

/**
 * Gets the part of a given string before the first occurrence of a given character.
 *
 * @param {string} sStrSearchIn     The string to search.
 * @param {string} sChar            The character to search for.
 * @param {boolean} bNullIfNotFound Whether null should be returned when the separator character is not found.
 *
 * @returns {string} The generated substring.
 */
export function SubstringBeforeCharFirst(sStrSearchIn: string, sChar: string, bNullIfNotFound: boolean): string | null {
    let sStrReturn = bNullIfNotFound ? null : sStrSearchIn
    //
    if (IsStringPrimitiveNonEmpty(sStrSearchIn)) {
        const iCharIndex = sStrSearchIn.indexOf(sChar)
        if (iCharIndex !== -1) {
            sStrReturn = sStrSearchIn.substring(0, iCharIndex)
        }
    }
    //
    return sStrReturn
}

/**
 * Gets the part of a given string before the last occurrence of a given character.
 *
 * @param {string} sStrSearchIn     The string to search.
 * @param {string} sChar            The character to search for.
 * @param {boolean} bNullIfNotFound Whether null should be returned when the separator character is not found.
 *
 * @returns {string} The generated substring.
 */
export function SubstringBeforeCharLast(sStrSearchIn: string, sChar: string, bNullIfNotFound: boolean): string | null {
    let sStrReturn = bNullIfNotFound ? null : sStrSearchIn
    //
    if (IsStringPrimitiveNonEmpty(sStrSearchIn)) {
        const iCharIndex = sStrSearchIn.lastIndexOf(sChar)
        if (iCharIndex !== -1) {
            sStrReturn = sStrSearchIn.substring(0, iCharIndex)
        }
    }
    //
    return sStrReturn
}

/**
 * Gets the part of a given string after the first occurrence of a given character.
 *
 * @param {string} sStrSearchIn     The string to search.
 * @param {string} sChar            The character to search for.
 * @param {boolean} bNullIfNotFound Whether null should be returned when the separator character is not found.
 *
 * @returns {string} The generated substring.
 */
export function SubstringAfterCharFirst(sStrSearchIn: string, sChar: string, bNullIfNotFound: boolean): string | null {
    let sStrReturn = bNullIfNotFound ? null : sStrSearchIn
    //
    if (IsStringPrimitiveNonEmpty(sStrSearchIn)) {
        const iCharIndex = sStrSearchIn.indexOf(sChar)
        if (iCharIndex !== -1) {
            sStrReturn = sStrSearchIn.substring(iCharIndex + 1)
        }
    }
    //
    return sStrReturn
}

/**
 * Gets the part of a given string after the last occurrence of a given character.
 *
 * @param {string} sStrSearchIn     The string to search.
 * @param {string} sChar            The character to search for.
 * @param {boolean} bNullIfNotFound Whether null should be returned when the separator character is not found.
 *
 * @returns {string} The generated substring.
 */
export function SubstringAfterCharLast(sStrSearchIn: string, sChar: string, bNullIfNotFound: boolean): string | null {
    let sStrReturn = bNullIfNotFound ? null : sStrSearchIn
    //
    if (IsStringPrimitiveNonEmpty(sStrSearchIn)) {
        const iCharIndex = sStrSearchIn.lastIndexOf(sChar)
        if (iCharIndex !== -1) {
            sStrReturn = sStrSearchIn.substring(iCharIndex + 1)
        }
    }
    //
    return sStrReturn
}

// Converts a general exception to a string.
//
export function ExceptionToString(pEx: IAkixiError | string): string {
    let sErrorMsg = ''
    if (pEx instanceof Error) {
        /*
        sErrorMsg = "";
        if (!IsStringEmpty(pEx.name))
            sErrorMsg += "Error name: " + pEx.name;
        if (!IsStringEmpty(pEx.message))
            sErrorMsg += "Error message: " + pEx.message;
        if (!IsStringEmpty(pEx.description))
            sErrorMsg += "Error description: " + pEx.description;
        */
        if (!IsStringEmpty(pEx.message)) sErrorMsg = pEx.message
        else if (!IsStringEmpty(pEx.description)) sErrorMsg = pEx.description
    } else if (!IsStringEmpty(pEx)) {
        sErrorMsg = pEx
    } else {
        sErrorMsg = 'Unknown error.'
    }
    const sTrimmed = sErrorMsg.trim()
    return sTrimmed === null ? '' : sTrimmed
}

// General exception handler.
//
export function ExceptionHandle(pEx: IAkixiError, sAction: string, bAssertDebugInfo = false): void {
    let sErrorOverall
    if (IsStringEmpty(sAction)) {
        sErrorOverall = 'An error occurred performing the requested action.'
    } else {
        sErrorOverall = `An error occurred performing action '${sAction}'.`
    }
    if (pEx instanceof Error) {
        let sErrorMsg = ''
        if (!IsVarEmpty(pEx.name)) {
            if (!IsStringEmpty(sErrorMsg)) sErrorMsg += '\n'
            sErrorMsg += `Error name: ${pEx.name}`
        }
        if (!IsVarEmpty(pEx.message)) {
            if (!IsStringEmpty(sErrorMsg)) sErrorMsg += '\n'
            sErrorMsg += `Error message: ${pEx.message}`
        } else if (!IsVarEmpty(pEx.description)) {
            if (!IsStringEmpty(sErrorMsg)) sErrorMsg += '\n'
            sErrorMsg += `Error description: ${pEx.description}`
        }
        //
        let sErrorLocation = ''
        if (bOUTPUT_DEBUG_INFO || bAssertDebugInfo) {
            if (!IsVarEmpty(pEx.fileName)) {
                if (!IsStringEmpty(sErrorLocation)) sErrorLocation += '\n'
                sErrorLocation += `File: ${pEx.fileName}`
            }
            if (!IsVarEmpty(pEx.lineNumber)) {
                if (!IsStringEmpty(sErrorLocation)) sErrorLocation += '\n'
                sErrorLocation += `Line: ${pEx.lineNumber}`
            }
        }
        //
        let sErrorStack = ''
        if (bOUTPUT_DEBUG_INFO || bAssertDebugInfo) {
            if (!IsVarEmpty(pEx.stack)) sErrorStack = pEx.stack
            else if (!IsVarEmpty(pEx.stacktrace)) sErrorStack = pEx.stacktrace
        }
        //
        if (!IsStringEmpty(sErrorMsg)) sErrorOverall += `\n\n${sErrorMsg}`
        if (!IsStringEmpty(sErrorLocation)) sErrorOverall += `\n\n${sErrorLocation}`
        if (!IsStringEmpty(sErrorStack)) sErrorOverall += `\n\n**********************\nStack Trace:\n${sErrorStack}`
    } else if (!IsStringEmpty(pEx)) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        sErrorOverall += `\n\nError : ${pEx as Error}`
    } else {
        sErrorOverall += '\n\nUnknown error.'
    }
    // eslint-disable-next-line
    alert(sErrorOverall)
}

// Disposes of any resources attached to an object instance by calling it's DeleteResources() method.
//
// eslint-disable-next-line
export function DisposeOf(pObject: any | null): void {
    if (!IsVarEmpty(pObject)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (!IsVarEmpty(pObject.DeleteResources)) {
            // eslint-disable-next-line
            pObject.DeleteResources()
        }
        pObject = null
    }
}

//
// Array variant.
//
// eslint-disable-next-line
export function DisposeOfArray(pArray: any[] | null): void {
    /* eslint-disable no-param-reassign */
    if (!IsVarEmpty(pArray)) {
        if (pArray instanceof Array) {
            for (let i = 0; i < pArray.length; i += 1) {
                DisposeOf(pArray[i])
                pArray[i] = null
                delete pArray[i]
            }
        } else {
            DisposeOf(pArray)
        }
        pArray = null
    }
}

PlaceInGlobalNameSpace('ExceptionHandle', ExceptionHandle)
PlaceInGlobalNameSpace('StrLeft', StrLeft)
PlaceInGlobalNameSpace('StrRight', StrRight)
PlaceInGlobalNameSpace('PadLeft', PadLeft)
PlaceInGlobalNameSpace('PadRight', PadRight)
PlaceInGlobalNameSpace('StringReplaceAll', StringReplaceAll)
PlaceInGlobalNameSpace('StringReplaceAllInChrsSeparatedList', StringReplaceAllInChrsSeparatedList)
PlaceInGlobalNameSpace('SubstringBeforeCharFirst', SubstringBeforeCharFirst)
PlaceInGlobalNameSpace('SubstringBeforeCharLast', SubstringBeforeCharLast)
PlaceInGlobalNameSpace('SubstringAfterCharFirst', SubstringAfterCharFirst)
PlaceInGlobalNameSpace('SubstringAfterCharLast', SubstringAfterCharLast)
PlaceInGlobalNameSpace('ExceptionToString', ExceptionToString)
PlaceInGlobalNameSpace('IsValidEmailAddress', IsValidEmailAddress)
PlaceInGlobalNameSpace('Integer64BitwiseAnd', Integer64BitwiseAnd)
PlaceInGlobalNameSpace('IsNumberChr', IsNumberChr)
PlaceInGlobalNameSpace('IsNumeric', IsNumeric)
PlaceInGlobalNameSpace('StringExpandPlaceholders', StringExpandPlaceholders)
PlaceInGlobalNameSpace('StringRepeat', StringRepeat)
PlaceInGlobalNameSpace('IsStringPrimitiveNonEmpty', IsStringPrimitiveNonEmpty)
PlaceInGlobalNameSpace('IsStringPrimitive', IsStringPrimitive)
PlaceInGlobalNameSpace('IsStringMatch', IsStringMatch)
PlaceInGlobalNameSpace('ControlEnable', ControlEnable)
PlaceInGlobalNameSpace('ControlHide', ControlHide)
PlaceInGlobalNameSpace('ManageActionButtonsBasedOnUserPermissions', ManageActionButtonsBasedOnUserPermissions)
PlaceInGlobalNameSpace('parseBool', parseBool)
PlaceInGlobalNameSpace('IsVarEmpty', IsVarEmpty)
PlaceInGlobalNameSpace('IsStringEmpty', IsStringEmpty)
PlaceInGlobalNameSpace('DisposeOf', DisposeOf)
PlaceInGlobalNameSpace('DisposeOfArray', DisposeOfArray)
